<template>
  <div id="week">
    <v-container fluid class="flex-grow-0 pa-0">
      <v-row class="ma-0 header">
        <v-col v-for="(day, date) in tasksByDay" :key="date" class="py-0">
          <v-row>
            <v-col cols="4 no-border flex-grow-1 d-flex flex-column justify-space-between">
              <div class="text-body-2 pt-1" :class="$utils.datePPF(date)">
                {{ $moment(date).format('ddd') }}
              </div>
              <div class="d-flex align-baseline pb-1">
                <v-menu location="left" open-on-hover>
                  <template v-slot:activator="{ props }">
                    <div v-bind="props" class="text-h4" :class="$utils.datePPF(date)">
                      {{ $moment(date).format('D') }}
                    </div>
                  </template>
                  <div class="staff-card">
                    <div
                      class="worker text-body-2"
                      v-for="(worker, key) in capacityObject[date].working
                        .concat(capacityObject[date].freelancers)
                        .concat(capacityObject[date].overtime)"
                      :key="key"
                    >
                      <div class="name">{{ worker.name }}</div>
                      <div class="hours ml-4 text-medium-emphasis">
                        {{ worker.hours }}
                      </div>
                    </div>
                    <div
                      class="worker text-body-2"
                      v-for="worker in capacityObject[date].holidays"
                      :key="worker.timetasticId"
                    >
                      <div class="name text-medium-emphasis text-decoration-line-through">
                        {{ worker.name }}
                      </div>
                      <div class="hours ml-4 text-medium-emphasis">H</div>
                    </div>
                  </div>
                </v-menu>
              </div>
            </v-col>
            <v-col cols="8" class="text-caption px-0 d-flex flex-column">
              <v-row>
                <v-col cols="7" class="no-border d-flex flex-column">
                  <div class="flex-grow-1 d-flex align-center justify-end">Cut</div>
                  <div class="text-right flex-grow-1 d-flex align-center justify-end">Workshop</div>
                </v-col>
                <v-col cols="5" class="d-flex flex-column text-body-2">
                  <div class="coloured cut flex-grow-1 d-flex align-center justify-center py-1">
                    {{ getCutMuCapacityPercentage(date) }}%
                  </div>
                  <div class="coloured workshop flex-grow-1 d-flex align-center justify-center py-1">
                    {{ getWorkshopMuCapacityPercentage(date) }}%
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <v-container fluid class="flex-grow-1 pa-0 d-flex flex-column">
      <v-row v-for="(taskTypeGroup, key) in taskTypeGroups" class="ma-0" :key="key">
        <v-col v-for="(day, date) in tasksByDay" :key="date" class="pa-0 d-flex flex-column">
          <div class="task-type text-body-2" :class="taskTypeGroup.name">
            <div>{{ getDailyUnitsByType(date, taskTypeGroup.name) }}</div>
            <div v-if="['cut', 'finish', 'pack', 'dispatch'].includes(taskTypeGroup.name)">
              <span class="mx-1">/</span>{{ getDailyMuByType(date, taskTypeGroup.name) }} mµ
            </div>
          </div>
          <draggable
            :list="tasksOfType(tasksByDay[date], taskTypeGroup)"
            :group="{ name: 'scheduled-tasks', pull: true, put: ['scheduled-tasks', 'unscheduled-tasks'] }"
            class="draggable-list flex-grow-1 scheduled-tasks"
            @end="onSortEnd"
            @add="onSortEnd"
            :id="date"
            :data-lane-types="taskTypeGroup.types.join(',')"
            item-key="id"
          >
            <template #item="{ element: task }">
              <div
                class="task text-body-2"
                :class="[
                  task.type,
                  $utils.camelize(task.subtype),
                  { coloured: !task.complete && !task.paused },
                  { paused: task.paused },
                  {
                    ghost: focussedTask !== null && task.shipmentID !== focussedTask.shipmentID
                  },
                  {
                    focussed: focussedTask !== null && task.shipmentID == focussedTask.shipmentID
                  }
                ]"
                :id="task.id"
              >
                <v-row>
                  <v-col cols="12" class="d-flex align-center">
                    <v-icon
                      v-if="task.type === 'cut' && !task.toolpathed && !task.repeatTaskId"
                      :size="14"
                      :style="{
                        'padding-bottom': '1px',
                        color: task.complete ? 'black' : 'white'
                      }"
                      >mdi-alert</v-icon
                    >
                    <v-icon
                      v-if="
                        task.type === 'dispatch' &&
                        task.subtype === 'To customer' &&
                        task.recutOrderNumber &&
                        !task.repeatTaskId
                      "
                      :size="14"
                      :style="{
                        'padding-bottom': '1px',
                        color: task.complete ? 'black' : 'white'
                      }"
                      >mdi-puzzle</v-icon
                    >
                    <v-icon
                      v-if="
                        task.type === 'dispatch' &&
                        task.subtype === 'To customer' &&
                        task.courierBooked &&
                        !task.complete &&
                        !task.repeatTaskId
                      "
                      :size="15"
                      :color="isShipmentPropertyTrue(task.shipmentID, 'balanceOutstanding') ? 'orange' : 'white'"
                      >mdi-truck-check</v-icon
                    >
                    <div class="mr-2" @click="$emit('focusTask', task)">
                      {{ task.orderNumber || task.invoiceNumber }}
                    </div>
                    <div class="flex-grow-1 d-flex" @click="$emit('openTask', task)">
                      <div>
                        {{ task.objectType }}
                        {{ task.subtype ? task.subtype : task.type }}
                      </div>
                      <v-spacer />
                      <div>
                        {{ task.units }}
                      </div>
                      <div v-if="['cut'].includes(task.type) && task.muCut">
                        <span class="mx-1">/</span>{{ $utils.toFixedNumber(task.muCut, 1) }} mµ
                      </div>
                      <div v-else-if="['finish', 'assemble'].includes(task.type) && task.muFinish">
                        <span class="mx-1">/</span>{{ $utils.toFixedNumber(task.muFinish, 1) }} mµ
                      </div>
                      <div v-else-if="['pack'].includes(task.type) && task.muPack">
                        <span class="mx-1">/</span>{{ $utils.toFixedNumber(task.muPack, 1) }} mµ
                      </div>
                      <div v-else-if="['dispatch'].includes(task.type) && task.muDispatch">
                        <span class="mx-1">/</span>{{ $utils.toFixedNumber(task.muDispatch, 1) }} mµ
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </template>
          </draggable>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: 'Week',
  components: { draggable },
  props: ['taskTypeGroups', 'tasksByDay', 'focussedTask', 'capacityObject', 'isShipmentPropertyTrue'],
  methods: {
    tasksOfType(tasks, taskTypeGroup) {
      return (tasks || []).filter((task) => taskTypeGroup.types.indexOf(task.type) >= 0)
    },
    getDailyUnitsByType(date, type) {
      if (this.tasksByDay[date]) {
        let tasks = this.tasksByDay[date]
        let units = 0
        for (const task of tasks) {
          if (task.type == type) {
            units += parseInt(task.units)
          }
        }
        return units
      }
    },
    getDailyMuByType(date, type) {
      if (this.tasksByDay[date]) {
        let tasks = this.tasksByDay[date]
        let mu = 0
        for (const task of tasks) {
          // add cut mu total
          if (['cut'].includes(task.type) && type == 'cut') {
            mu += task?.muCut ? task?.muCut : 0
          }
          // add assemble mu to finish mu total
          if (['finish', 'assemble'].includes(task.type) && type == 'finish') {
            mu += task?.muFinish ? task?.muFinish : 0
          }
          // add pack mu total
          if (['pack'].includes(task.type) && type == 'pack') {
            mu += task?.muPack ? task?.muPack : 0
          }
          // add dispatch mu total
          if (['dispatch'].includes(task.type) && type == 'dispatch') {
            mu += task?.muDispatch ? task?.muDispatch : 0
          }
        }
        mu = this.$utils.toFixedNumber(mu, 1)
        return mu
      }
    },
    getWorkshopMuRequirement(date) {
      let workshopMuTotal = 0

      for (const task of this.tasksByDay[date]) {
        switch (task.type) {
          case 'finish':
          case 'assemble':
            workshopMuTotal += task?.muFinish || 0
            break
          case 'pack':
            workshopMuTotal += task?.muPack || 0
            break
          case 'dispatch':
            workshopMuTotal += task?.muDispatch || 0
            break
          default:
            break
        }
      }

      return Number(workshopMuTotal.toFixed(1))
    },
    workshopMuCapacityForDay(day) {
      if (day) {
        let hours = 0
        let workers = day?.working.concat(day?.freelancers).concat(day?.overtime)
        for (const worker of workers) {
          hours += worker.hours
        }
        hours -= day?.cutting // remove cutting hours (only)
        return hours * (this.$store.state.mu.workshopCapacity / 8) // 8 hour day
      }
    },

    getCutMuCapacityPercentage(date) {
      const muCut = this.tasksByDay[date].reduce((sum, val) => {
        return val.type === 'cut' && val?.muCut ? sum + parseInt(val?.muCut) : sum
      }, 0)
      return Math.round((muCut / this.$store.state.mu.cutCapacity) * 100)
    },
    getWorkshopMuCapacityPercentage(date) {
      const capacity = this.workshopMuCapacityForDay(this.capacityObject[date])
      const mu = this.getWorkshopMuRequirement(date)
      const workshopMuPercentage = capacity && mu ? Math.round((mu / capacity) * 100) : 0

      return workshopMuPercentage
    },
    onSortEnd(e) {
      this.$emit('onSortEnd', e)
    }
  }
}
</script>

<style lang="scss" scoped>
// Capacity input styling
::v-deep .v-text-field {
  flex-grow: 0;
  width: 25px;
  input {
    padding: 0;
    text-align: right;
  }
}

.staff-card {
  display: flex;
  flex-direction: column;
  background: white;
  padding: 4px 6px;
  cursor: pointer;
  .worker {
    display: flex;
    justify-content: space-between;
    padding: 2px 0px;
  }
}

#week {
  position: absolute;
  overflow-y: scroll;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: width 0.5s;
  .v-col {
    border-right: solid 1px $ruleGrey;
    &:last-child,
    &.no-border {
      border: none;
    }
    // One row, mµ to the right: without the flex box these two divs stack and sit left.
    .task-type {
      background: $sand;
      border-top: solid 1px $ruleGrey;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding: 4px;
    }
    .count {
      white-space: nowrap;
    }
  }
}
</style>
